import {useState, useEffect} from 'react'
import { useNavigate } from 'react-router-dom';

import { adminAPI } from '../../../api';

import {APIAccessAddModal} from './APIAccessAddModal'

import { GridColDef } from '@mui/x-data-grid';
import { convertToDateTime, StyledDataGrid, dataGridCellClassName, dataGridHeaderClassName } from '../../../utils'

import { RateLimitModelType } from '../../../models';

interface Props {
}

const APIAccesses : React.FC<Props> = ({}) => {

  const [loaded, setLoaded] = useState<boolean>(false)
  const [hasNoData, setHasNoData] = useState<boolean>(false)
  const [projects, setProjects] = useState<any[]>([])
  const [defaultSettingProject, setDefaultSettingProject] = useState<any>(null)
  const [showProjectAddModal, setShowProjectAddModal] = useState<boolean>(false)
  const navigate = useNavigate();

  useEffect(() => {
    loadDefaultSettingProject()
    loadProjects()
  }, [])

  async function loadProjects(){
    let {data: data} = await adminAPI.getProjects()
    setProjects(data['data'])
    setLoaded(true)
  }

  async function loadDefaultSettingProject(){
    if( !defaultSettingProject ){
      let {data: data} = await adminAPI.getProject('default')
      setDefaultSettingProject(data['data'])
    }
  }

  const content_settings = [
    "prompt_intervention",
    "prompt_safety_filter",
    "prompt_blocklist_filter",
    "prompt_rephrasing",
    "classifier_driven_generation",
    "islamic_rag",
    "recency_rag",
    "biography_rag",
    "poem",
    "text_to_speech",
    "speech_to_text",
    "transcription",
    "image_generation",
    "image_understanding",
    "video_understanding",
    "deep_thinking",
    "translation",
    "attribution",
    "response_blocklist_filter",
    "response_safety_filter",
    "response_quran_validator",
  ];

  const columnsForMessages: GridColDef[] = [
    { field: 'name', headerName: 'Name', headerClassName: dataGridHeaderClassName + ' sticky-left', cellClassName: dataGridCellClassName + ' sticky-left', minWidth: 200, flex:1,
      renderCell: (params) => {
        return <>
        {params.row.for_load_testing && <span className={`badge badge-light-warning me-1`}>Load Testing</span>}
        {params.value}
        </>
      }
    },
    { field: "created_by_email", headerName: 'Created/Approved By', headerClassName: dataGridHeaderClassName, cellClassName: dataGridCellClassName, minWidth: 200},
    { field: "created_dt", headerName: 'Created/Approved On', headerClassName: dataGridHeaderClassName, cellClassName: dataGridCellClassName, minWidth: 180,
      renderCell: (params) => {
        return convertToDateTime(params.value)
      }
    },
    { field: "api_access_request_full_name", headerName: 'Requested By', headerClassName: dataGridHeaderClassName, cellClassName: dataGridCellClassName, minWidth: 200},
    { field: 'deleted_dt', headerName: 'Status', headerClassName: dataGridHeaderClassName, cellClassName: dataGridCellClassName, minWidth: 150,
      renderCell: (params) => {
        return <span className={`badge badge-${params.value == 'deleted' ? 'danger' : 'success'}`}>
          {params.value}
        </span>
      }, 
      valueGetter: (value) => {
        return value ? 'deleted' : 'active'
      }
    },
    { field: 'api_access_status', headerName: 'Key Status', headerClassName: dataGridHeaderClassName, cellClassName: dataGridCellClassName, minWidth: 150,
      renderCell: (params) => {
        return <span className={`badge badge-${params.value == 'active' ? 'success' : 'danger'}`}>
          {params.value}
        </span>
      },
      valueGetter: (value) => {
        return value ? value : 'deleted'
      }
    },
    { field: 'number_of_api_requests', headerName: '# Requests', headerClassName: dataGridHeaderClassName, cellClassName: dataGridCellClassName, minWidth: 150, 
      renderCell: (params) => {
        return params.value ? params.value.toLocaleString() : 0
      }
    },
    { field: 'latest_api_request_dt', headerName: 'Latest Request On', headerClassName: dataGridHeaderClassName, cellClassName: dataGridCellClassName, minWidth: 180,
      renderCell: (params) => {
        return convertToDateTime(params.value)
      }
    },
    ...content_settings.map((setting) => ({
      field: setting,
      headerName: setting.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase()),
      headerClassName: dataGridHeaderClassName,
      cellClassName: dataGridCellClassName,
      minWidth: 150,
      renderCell: (params) => {
        const isDefault = !params.row[setting + '_updated_by'];
        return (
          <>
            <span className={`badge border border-gray-400 w-60px flex-center ${params.value.startsWith('enabled') ? 'badge-primary' : 'badge-secondary'}`}>
              {params.value.replace(' (default)', '')}
            </span>
            {isDefault && (
              <span className="badge badge-light border border-gray-400 ms-1">default</span>
            )}
          </>
        );
      },
      valueGetter: (v, row) => {
        const value = v ? 'enabled' : 'disabled';
        const isDefault = !row[setting + '_updated_by'];
        return isDefault ? `${value} (default)` : value;
      }
    })),
    ...Object.values(RateLimitModelType).map((field: string) => ({
      field: `api_access_rate_limit.${field}`,
      headerName: field.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase()),
      headerClassName: dataGridHeaderClassName,
      cellClassName: dataGridCellClassName,
      minWidth: 150,
      renderCell: (params) => {
        const rateLimit = params.row.api_access_rate_limit?.[field]
        if (rateLimit?.value === null) {
          return <div className="badge badge-warning border border-gray-400">unlimited</div>
        }
        if (rateLimit === undefined) {
          return <div className="badge badge-light border border-gray-400">default</div>
        }
        return rateLimit.value.toLocaleString() + ' per ' + rateLimit.unit
      },
      valueGetter: (value, row) => {
        const rateLimit = row.api_access_rate_limit?.[field]
        if (rateLimit?.value === null) {
          return "unlimited"
        }
        if (rateLimit === undefined) {
          return "default"
        }
        return rateLimit;
      }
    }))
  ];

  const handleRowClick = (params: any) => {
    navigate(params.row.id)
  };

  return (
    <div className="row">
      <div className="col-12">
        <div className="d-flex justify-content-between">
          <button className="btn btn-sm btn-light-dark" onClick={()=>setShowProjectAddModal(true)}>
            Add
          </button>
          <button className="btn btn-sm btn-light-dark" onClick={()=>navigate('default')}>
            Default Settings
          </button>
        </div>
        <div className="row mt-5">
          {!hasNoData && projects && (
              <StyledDataGrid
                getRowId={(row) => row.id}
                rows={projects}
                onRowClick={handleRowClick}
                columns={columnsForMessages}
                disableColumnMenu={false}
                getRowHeight={() => 'auto'}
                disableRowSelectionOnClick
                sx={{
                  border: 0,
                  '& .MuiDataGrid-row:hover': {
                    cursor: 'pointer',
                  },
                }}
                initialState={{
                  pagination: {
                    paginationModel: { page: 0, pageSize: 50 },
                  }
                }}
                slotProps={{
                  toolbar: {
                    showQuickFilter: true,
                  },
                }}
                columnGroupingModel={[
                  {
                    groupId: 'Overview',
                    headerClassName: dataGridHeaderClassName,
                    children: [
                      { field: 'name' },
                      { field: 'created_by_email' },
                      { field: 'created_dt' },
                      { field: 'api_access_request_full_name' },
                      { field: 'deleted_dt' }
                    ],
                  },
                  {
                    groupId: 'Usage',
                    headerClassName: dataGridHeaderClassName,
                    children: [
                      { field: 'api_access_status' },
                      { field: 'number_of_api_requests' },
                      { field: 'latest_api_request_dt' }
                    ],
                  },
                  {
                    groupId: 'Settings',
                    headerClassName: dataGridHeaderClassName,
                    children: content_settings.map(setting => ({
                      field: setting
                    })),
                  },
                  {
                    groupId: 'Rate Limits',
                    headerClassName: dataGridHeaderClassName,
                    children: Object.values(RateLimitModelType).map(field => ({
                      field: `api_access_rate_limit.${field}`
                    })),
                  }
                ]}
                pageSizeOptions={[5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 100, 150, 200]}
              />
            )}
        </div>
      </div>
      <APIAccessAddModal show={showProjectAddModal} handleClose={() => setShowProjectAddModal(false)} handleAdded={loadProjects}/>
    </div>
  )
}

export default APIAccesses